import React, { useEffect, useMemo, useRef } from 'react';
import TextBox from 'devextreme-react/text-box';
import SelectBox from 'devextreme-react/select-box';
import DataSource from 'devextreme/data/data_source';
import DateBox from 'devextreme-react/date-box';
import NumberBox from 'devextreme-react/number-box';
import TextArea from 'devextreme-react/text-area';
import { CheckBox, Switch, Validator } from 'devextreme-react';
import { RequiredRule } from 'devextreme-react/data-grid';
import moment from 'moment';
import { useAppSelector } from '../../../contexts/hooks';
import { formatMessage } from 'devextreme/localization';
import { SystemFields, TypeCombobox } from '../../../types/types.config';

// A partir de este número de filas una lista se trata como "catálogo grande" (CIE10, CUPS,
// etc.). El combo entonces no dibuja la lista completa al abrirse: pide al menos 2 caracteres,
// filtra sobre el arreglo en memoria y muestra los resultados paginados de 50 en 50 con carga
// al hacer scroll. Por debajo del umbral el comportamiento es el de siempre (lista completa
// visible al abrir, búsqueda desde el primer carácter).
const LARGE_LIST_THRESHOLD = 300;
const LARGE_LIST_PAGE_SIZE = 50;
const LARGE_LIST_MIN_SEARCH = 2;

// Los valores de los combos llegan como texto desde los campos dinámicos. Se convierten a
// número cuando lo son (ids), y se dejan tal cual cuando la clave es alfanumérica.
const toComboKey = (v: any) => {
  if (v == null || v === '') return undefined;
  if (typeof v === 'number') return v;
  const n = Number(v);
  return Number.isNaN(n) ? v : n;
};

export const CustomFormControl = ({ data, onValueChange, isEditing, value, valueDepency, defaultValue }: { data: SystemFields; onValueChange: (e: any) => void; isEditing: boolean; value?: any; defaultValue?: any; valueDepency?: any }) => {
  // Sin fallback `|| []` aquí: crearía un arreglo nuevo en cada render y con él un DataSource
  // nuevo por render, lo que reinicia el combo constantemente. El fallback va dentro del memo.
  const cmb = useAppSelector((x) => x.configuration.Data?.ComboBox);
  const dateFormat = useAppSelector((x) => x.configuration.Data?.Config_General.find((c) => c.Cgn_Nombre === 'DateTimeClientFormat')?.Cgn_Resultado);
  const refSelectBox = useRef<SelectBox>(null);

  const isSelectOrLookup = data.TipoCampo.Tcm_Equivalencia === 'dxSelectBox' || data.TipoCampo.Tcm_Equivalencia === 'dxLookup';
  const comboTable = data.TablasComboBox?.Tbc_NombreTabla;
  const comboKeyField = data.TablasComboBox?.Tbc_CampoId;

  // Filas del combo (con el filtro de dependencia aplicado). useMemo en lugar de estado +
  // efecto: no se copia el arreglo (con catálogos de miles de filas cada copia y cada render
  // extra se notan) y solo se recalcula cuando cambia la tabla, la dependencia o el catálogo.
  const comboItems = useMemo<any[]>(() => {
    if (!isSelectOrLookup) return [];
    const comboList: TypeCombobox[] = cmb ?? [];
    const comboSource = comboList.find((x) => x.Name === comboTable)?.Data;
    if (!comboSource || !Array.isArray(comboSource)) return [];
    if (data.Dfc_Dependency && valueDepency != null) {
      const depValue = typeof valueDepency === 'string' ? parseInt(valueDepency, 10) : valueDepency;
      return comboSource.filter((item) => item[data.Dfc_DependencyField] === depValue);
    }
    return comboSource;
  }, [cmb, isSelectOrLookup, comboTable, data.Dfc_Dependency, data.Dfc_DependencyField, valueDepency]);

  const isLargeList = comboItems.length > LARGE_LIST_THRESHOLD;

  // DataSource paginado sobre el arreglo: el desplegable solo renderiza una página y va
  // cargando más al hacer scroll; la búsqueda filtra en memoria antes de dibujar. Con la
  // clave definida, resolver el texto del valor seleccionado no depende de la página cargada.
  const comboDataSource = useMemo(() => {
    if (!isSelectOrLookup) return undefined;
    return new DataSource({
      store: comboItems,
      key: comboKeyField,
      paginate: true,
      pageSize: isLargeList ? LARGE_LIST_PAGE_SIZE : Math.max(comboItems.length, 1),
    });
  }, [isSelectOrLookup, comboItems, comboKeyField, isLargeList]);

  useEffect(() => {
    return () => {
      comboDataSource?.dispose();
    };
  }, [comboDataSource]);

  // Limpieza de valor solo cuando cambia la dependencia
  useEffect(() => {
    if (data.Dfc_Dependency && data.TipoCampo.Tcm_Equivalencia === 'dxSelectBox' && valueDepency === null && value != null) {
      onValueChange(null);
    }
  }, [valueDepency]);

  switch (data.TipoCampo.Tcm_Equivalencia) {
    case 'dxSelectBox':
    case 'dxLookup': {
      const valueExpr = data.TablasComboBox.Tbc_CampoId;
      const displayExpr = data.TablasComboBox.Tbc_CampoVisible;

      return (
        <SelectBox
          ref={refSelectBox}
          defaultValue={toComboKey(defaultValue)}
          stylingMode='outlined'
          labelMode='static'
          searchEnabled
          searchExpr={displayExpr}
          searchTimeout={isLargeList ? 300 : undefined}
          // Catálogos grandes: nada de lista completa al abrir; se pide texto y se filtra.
          minSearchLength={isLargeList ? LARGE_LIST_MIN_SEARCH : 0}
          showDataBeforeSearch={!isLargeList}
          value={toComboKey(value)}
          dataSource={comboDataSource}
          valueExpr={valueExpr}
          displayExpr={displayExpr}
          label={data.Dfc_Label}
          placeholder={isLargeList ? formatMessage('typeToSearch') : undefined}
          onValueChange={onValueChange}
          readOnly={!isEditing || !data.Dfc_Editable}
        >
          {data.Dfc_Requerido && (
            <Validator>
              <RequiredRule message={`${data.Dfc_Label} ${formatMessage('requiredField')}`} />
            </Validator>
          )}
        </SelectBox>
      );
    }

    case 'dxDateBox':
      return (
        <DateBox stylingMode='outlined' defaultValue={defaultValue} maskInvalidMessage={formatMessage('invalidFormat')} placeholder={moment().format(dateFormat?.toUpperCase() || '')} useMaskBehavior labelMode='static' type='date' displayFormat={dateFormat} value={value} invalidDateMessage={formatMessage('invalidFormat')} label={data.Dfc_Label} onValueChange={onValueChange} readOnly={!isEditing || !data.Dfc_Editable} disabled={!isEditing || !data.Dfc_Editable}>
          {data.Dfc_Requerido && (
            <Validator>
              <RequiredRule message={`${data.Dfc_Label} ${formatMessage('requiredField')}`} />
            </Validator>
          )}
        </DateBox>
      );

    case 'dxNumberBox':
      return (
        <NumberBox stylingMode='outlined' defaultValue={defaultValue}  format={data.Dfc_Formato || undefined} labelMode='static' value={value} validationError={null} maskInvalidMessage={formatMessage('invalidFormat')} label={data.Dfc_Label} onValueChange={onValueChange} readOnly={!isEditing || !data.Dfc_Editable} mask={data.Dfc_Mascara || ''}>
          {data.Dfc_Requerido && (
            <Validator>
              <RequiredRule message={`${data.Dfc_Label} ${formatMessage('requiredField')}`} />
            </Validator>
          )}
        </NumberBox>
      );

    case 'dxCheckBox':
      return (
        <>
          <div className='dx-field-label'>{data.Dfc_Label}</div>
          <CheckBox defaultValue={defaultValue || false} stylingMode='outlined' value={value} validationError={null} onValueChange={onValueChange} readOnly={!isEditing || !data.Dfc_Editable} />
        </>
      );

    case 'dxSwitch':
      return (
        <>
          <div className='dx-field-label'>{data.Dfc_Label}</div>
          <Switch stylingMode='outlined' value={value} defaultValue={defaultValue || false} validationError={null} onValueChange={onValueChange} readOnly={!isEditing || !data.Dfc_Editable} />
        </>
      );

    case 'dxTextArea':
      return (
        <TextArea stylingMode='outlined' defaultValue={defaultValue} maxLength={data.Dfc_Maximo ? parseInt(data.Dfc_Maximo, 10) : 10000} width='100%' height={100} labelMode='static' value={value} validationError={null} maskInvalidMessage={formatMessage('invalidFormat')} label={data.Dfc_Label} onValueChange={onValueChange} readOnly={!isEditing || !data.Dfc_Editable} mask={data.Dfc_Mascara || ''}>
          {data.Dfc_Requerido && (
            <Validator>
              <RequiredRule message={`${data.Dfc_Label} ${formatMessage('requiredField')}`} />
            </Validator>
          )}
        </TextArea>
      );

    default:
      return (
        <TextBox stylingMode='outlined' defaultValue={defaultValue} maxLength={data.Dfc_Maximo ? parseInt(data.Dfc_Maximo, 10) : 5000} labelMode='static' value={value} validationError={null} maskInvalidMessage={formatMessage('invalidFormat')} label={data.Dfc_Label} onValueChange={onValueChange} readOnly={!isEditing} mask={data.Dfc_Mascara || ''}>
          {data.Dfc_Requerido && (
            <Validator>
              <RequiredRule message={`${data.Dfc_Label} ${formatMessage('requiredField')}`} />
            </Validator>
          )}
        </TextBox>
      );
  }
};

export default CustomFormControl;
